/**
 * CompareModels — one-click A/B: runs the SAME run through two chosen models for
 * a given generator and shows both outputs + latency side-by-side. Non-persisting
 * (never changes the live version). Used to judge quality before switching models.
 */
import React, { useCallback, useEffect, useState } from "react";
import { GitCompare, ChevronDown, ChevronUp, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const MODELS = [
  { key: "opus", label: "Opus 4.8" },
  { key: "sonnet", label: "Sonnet 4.6" },
  { key: "haiku", label: "Haiku 4.5" },
];

function Side({ side, data }) {
  if (!data) return null;
  return (
    <div className="flex-1 min-w-0 border border-zinc-200" data-testid={`compare-side-${side}`}>
      <div className="flex items-center justify-between px-2.5 py-1.5 bg-zinc-900 text-white text-[11px]">
        <span className="font-medium">{data.model_label}</span>
        <span className="flex items-center gap-1.5">
          {data.ok ? <CheckCircle2 size={12} className="text-emerald-400"/> : <XCircle size={12} className="text-red-400"/>}
          {data.latency_ms} ms
        </span>
      </div>
      {data.ok ? (
        <pre className="text-[10px] leading-snug p-2 overflow-auto max-h-[360px] whitespace-pre-wrap break-words text-zinc-700">
{JSON.stringify(data.payload, null, 2)}
        </pre>
      ) : (
        <div className="p-2.5 text-[11px] text-red-700">{data.error}</div>
      )}
    </div>
  );
}

export default function CompareModels({ runId, generator, label }) {
  const [open, setOpen] = useState(false);
  const [a, setA] = useState("opus");
  const [b, setB] = useState("sonnet");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [costs, setCosts] = useState(null);

  const loadDefaults = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/assessments/llm-models`);
      setDefaults(data.defaults);
      setCosts(data.cost_estimates?.[generator] || null);
      if (data.defaults?.[generator]) setA(data.defaults[generator]);
    } catch { /* non-critical */ }
  }, [generator]);

  useEffect(() => { if (open && !defaults) loadDefaults(); }, [open, defaults, loadDefaults]);

  const run = async () => {
    if (a === b) { toast.error("Pick two different models to compare"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/admin/assessments/runs/${runId}/compare/${generator}`, { model_a: a, model_b: b });
      setResult(data);
      if (data.a.ok && data.b.ok) toast.success("Compare complete — review both outputs");
      else toast.warning("Compare finished with errors (see each side)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compare failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-dashed border-zinc-300 p-4" data-testid={`compare-panel-${generator}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2" data-testid={`compare-toggle-${generator}`}>
        <span className="flex items-center gap-2 text-zinc-600 text-[11px] font-medium uppercase tracking-[0.14em]">
          <GitCompare size={13}/> Compare models · {label}
        </span>
        {open ? <ChevronUp size={15} className="text-zinc-400"/> : <ChevronDown size={15} className="text-zinc-400"/>}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <div className="flex items-end gap-2 flex-wrap">
            <div className="w-full">
              <label className="block text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Model A</label>
              <select value={a} onChange={(e) => setA(e.target.value)} className="w-full max-w-full box-border border border-zinc-300 text-[12px] px-2 py-1.5 truncate" data-testid={`compare-a-${generator}`}>
                {MODELS.map((m) => <option key={m.key} value={m.key}>{m.label}{defaults?.[generator] === m.key ? " (default)" : ""}</option>)}
              </select>
            </div>
            <div className="w-full">
              <label className="block text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Model B</label>
              <select value={b} onChange={(e) => setB(e.target.value)} className="w-full max-w-full box-border border border-zinc-300 text-[12px] px-2 py-1.5 truncate" data-testid={`compare-b-${generator}`}>
                {MODELS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
            <button type="button" onClick={run} disabled={busy} className="ek-btn ek-btn-primary text-[12px] disabled:opacity-50" data-testid={`compare-run-${generator}`}>
              {busy ? <Loader2 size={13} className="animate-spin"/> : <GitCompare size={13}/>}
              {busy ? "Running both…" : "Run compare"}
            </button>
          </div>
          <p className="text-[10.5px] text-zinc-400">
            Runs both models on this run (non-persisting — your live version is untouched).
            {costs && (
              <span> Indicative cost: ~${costs[a]} + ~${costs[b]} = <strong>~${(costs[a] + costs[b]).toFixed(2)}</strong>.</span>
            )}
          </p>
          {result && (
            <div className="flex gap-2 items-stretch" data-testid={`compare-result-${generator}`}>
              <Side side="a" data={result.a} />
              <Side side="b" data={result.b} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
